import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { SidePanelType } from '../types';
import {
  selectPinnedPanels,
  selectPinnedPanelsAvailable,
  selectSidePanel,
  sidePanelClosed,
  sidePanelOpened,
  sidePanelPinned,
  sidePanelUnpinned,
} from '../sidePanelsSlice';

export const useSidePanel = () => {
  const dispatch = useAppDispatch();
  const sidePanel = useAppSelector(selectSidePanel);
  const pinnedPanels = useAppSelector(selectPinnedPanels);
  const pinnedPanelsAvailable = useAppSelector(selectPinnedPanelsAvailable);

  const openPanel = useCallback(
    (type: SidePanelType) => {
      dispatch(sidePanelOpened(type));
    },
    [dispatch]
  );

  const closePanel = useCallback(
    (type: SidePanelType) => {
      dispatch(sidePanelClosed(type));
    },
    [dispatch]
  );

  const togglePanel = useCallback(
    (type: SidePanelType) => {
      if (pinnedPanels.includes(type)) {
        return;
      }

      if (sidePanel === type) {
        dispatch(sidePanelClosed(type));
      } else {
        dispatch(sidePanelOpened(type));
      }
    },
    [dispatch, sidePanel, pinnedPanels]
  );

  const pinPanel = useCallback(
    (type: SidePanelType) => {
      dispatch(sidePanelPinned(type));
    },
    [dispatch]
  );

  const unpinPanel = useCallback(
    (type: SidePanelType) => {
      dispatch(sidePanelUnpinned(type));
    },
    [dispatch]
  );

  const isPanelVisible = useCallback(
    (type: SidePanelType) => sidePanel === type || pinnedPanels.includes(type),
    [sidePanel, pinnedPanels]
  );

  return {
    sidePanel,
    pinnedPanels,
    openPanel,
    closePanel,
    togglePanel,
    pinPanel,
    unpinPanel,
    isPanelVisible,
    pinnedPanelsAvailable,
  };
};
